import { createActionCreator, createGenericActionCreator } from '@pressreader/appstore';
import type { NavConfig, NavConfigRefocusConfigAny, NavConfigRefocusTargetAny, ViewName } from '@pressreader/navigation-types';

import { type RouteIntent, State, To, Update } from '../types';

const prefix = '@pressreader/navigation-core/';

interface RouteChangeBeganPayload {
    navState: { state?: Record<string, unknown>; stateLess?: Record<string, unknown>; viewName: ViewName };
}

interface RouteChangeDonePayload {
    newConfig: NavConfig;

    /** Route changed to legacy view. */
    isLegacyRoute: boolean;

    /** What route finalization decided for this route, carried to the entry activation that executes it. */
    intents?: RouteIntent[];
}

interface EntryActivatedPayload {
    /** Identity of the history entry being activated. */
    entryKey: string;

    /** Identity of THIS activation of that entry (`nextActivationId`), which a reload advances. */
    activationId: string;

    config: NavConfig;

    /** Instructions route finalization left for the orchestrator to execute on this activation. */
    intents?: RouteIntent[];
}

interface UpdateNavConfigPayload {
    newNavConfig: NavConfig;

    /** Whether to update url or not. Or use another NavConfig for url generation. */
    urlNavConfig?: boolean | NavConfig;
}

interface NavConfigUpdatedPayload {
    newNavConfig: NavConfig;
}

interface NavigateToPayload {
    resetState?: boolean;

    replace?: boolean;

    viewName: ViewName;
    // Type-unsafe — allows passing wrong property names without compile errors (e.g. `result` instead of `checkout`).
    // Ideally should be generic and constrained to the target view's NavConfig type.
    params?: any;
}

interface NavigateToLinkPayload {
    link: string;

    openNewWindow?: boolean;
}

interface NavigateHomePayload {
    replace?: boolean;
}

interface WillNavigatePayload {
    currentConfig: NavConfig;
}

interface NavigateBeganPayload {
    isLegacyRoute?: boolean;

    prevConfig: NavConfig | null;
    config: NavConfig;
}

interface NavigateDonePayload {
    isLegacyRoute?: boolean;

    prevConfig: NavConfig | null;
    config: NavConfig;
}

interface NavigateFailedPayload {
    navigateFailed: true;
    error: unknown;

    config: NavConfig;
    prevConfig: NavConfig | null;
}

interface NavigateInterruptedPayload {
    reason: 'redirected';

    config: NavConfig;
}

interface HistoryPayload {
    to: To;
    state?: State;
}

interface NavigateToViewParams {
    [key: string]: unknown;

    cacheKey?: string;
    referrer?: string;
    replaced?: boolean;
    viewName?: string;
}

interface NavigateToViewPayload {
    viewName: ViewName;
    params: NavigateToViewParams;
    replace?: boolean;
    ignoreIssueInfo?: boolean;
    isForceReplace?: boolean;
}

interface UpdateStatePayload {
    params: Record<string, any>;
    reload?: boolean;
    url?: string;
}

export const routeChangeBegan = createActionCreator<RouteChangeBeganPayload>(`${prefix}route_change_began`);
export const routeChangeDone = createActionCreator<RouteChangeDonePayload>(`${prefix}route_change_done`);

/**
 * One history entry became the live one, under `config` — dispatched for every activation, including
 * a back/forward POP and a replace-with-reload of the entry already on screen, on both the
 * full-navigation and the skip-navigation branches. Orchestrators that must run per activation rather
 * than per view transition listen to this and carry `activationId` through their async work, so a
 * continuation can be matched to the activation that started it.
 *
 * A route superseded while its resolution is still pending never activates: `onRouteChangeBegan`
 * discards it before any history write.
 */
export const entryActivated = createActionCreator<EntryActivatedPayload>(`${prefix}entry_activated`);

export const updateNavConfigAction = createActionCreator<UpdateNavConfigPayload>(`${prefix}update_nav_config`);
export const navConfigUpdated = createActionCreator<NavConfigUpdatedPayload>(`${prefix}nav_config_updated`);

export const navigateBackAction = createActionCreator(`${prefix}navigate_back`);

export const navigateToAction = createActionCreator<NavigateToPayload>(`${prefix}navigate_to`);
export const navigateToPreviousViewAction = createActionCreator<{
    /**
     * If failed to find a previous view configuration to navigate to, go to Home view.
     * @default true
     */
    useOpenHomeViewFallback?: boolean;
    /**
     * If failed to find a previous view in history, go back in history instead of navigating back in history stack.
     * @default false
     */
    useGoBackInHistoryFallback?: boolean;
}>(`${prefix}navigate_to_previous`);
export const navigateToLinkAction = createActionCreator<NavigateToLinkPayload>(`${prefix}navigate_to_link`);
export const navigateHomeAction = createActionCreator<NavigateHomePayload>(`${prefix}navigate_home`);

export const willNavigateEvent = createActionCreator<WillNavigatePayload>(`${prefix}will_navigate`);

export const navigateBegan = createGenericActionCreator<NavigateBeganPayload>(`${prefix}navigate_began`);
export const navigateDone = createGenericActionCreator<NavigateDonePayload>(`${prefix}navigate_done`);
export const navigateFailed = createGenericActionCreator<NavigateFailedPayload>(`${prefix}navigate_failed`);
export const navigateInterrupted = createGenericActionCreator<NavigateInterruptedPayload>(`${prefix}navigate_redirected`);

export const updateLocationAction = createActionCreator<Update>(`${prefix}location_update`);
export const pushStateToHistoryAction = createActionCreator<HistoryPayload>(`${prefix}history_push`);
export const replaceStateHistoryAction = createActionCreator<HistoryPayload>(`${prefix}history_replace`);
export const replaceStateWithReloadHistoryAction = createActionCreator<HistoryPayload>(`${prefix}history_replace_reload`);
export const goHistoryAction = createActionCreator<number>(`${prefix}history_go`);
export const backHistoryAction = createActionCreator(`${prefix}history_back`);
export const forwardHistoryAction = createActionCreator(`${prefix}history_forward`);
export const goViewAction = createActionCreator<NavigateToViewPayload>(`${prefix}history_go_view`);
export const goViewDone = createActionCreator<NavigateToViewPayload>(`${prefix}history_go_view_done`);
export const resetStateHistoryAction = createActionCreator(`${prefix}history_reset_state`);
export const updateStateHistoryAction = createActionCreator<UpdateStatePayload>(`${prefix}history_update_state`);

export const refocusConfigsAddAction = createActionCreator<NavConfigRefocusConfigAny>(`${prefix}refocus_configs_add_action`);
export const refocusConfigsRemoveAction = createActionCreator<{
    reason: string;
    config: NavConfigRefocusConfigAny;
}>(`${prefix}refocus_configs_remove_action`);

export const refocusTargetsAddAction = createActionCreator<NavConfigRefocusTargetAny>(`${prefix}refocus_targets_add_action`);
export const refocusTargetsRemoveAction = createActionCreator<{
    reason: string;
    target: NavConfigRefocusTargetAny;
}>(`${prefix}refocus_targets_remove_action`);

export const refocusIsPerforming = createActionCreator<NavConfigRefocusTargetAny>(`${prefix}refocus_is_performing`);
export const refocusPerformed = createActionCreator<NavConfigRefocusTargetAny>(`${prefix}refocus_performed`);

// These are required since their respective action creators are generic.
export type { NavigateBeganPayload, NavigateDonePayload, NavigateFailedPayload, NavigateInterruptedPayload, NavigateToPayload };
